import { useState } from "react";
import { Check, Star, Zap, BookOpen, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const fullAccessOptions = [
  { label: "Mensal", price: "14,99", period: "/mês", equiv: null, tag: null, packageId: "full_monthly" },
  { label: "3 Meses", price: "39,99", period: "/3 meses", equiv: "≈ €13,33/mês", tag: null, packageId: "full_3months" },
  { label: "6 Meses", price: "69,99", period: "/6 meses", equiv: "≈ €11,66/mês", tag: "Melhor valor", packageId: "full_6months" },
];

const plans = [
  {
    id: "flex",
    name: "Pack Flex",
    subtitle: "até 6 módulos",
    icon: Zap,
    price: "11,99",
    period: "/mês",
    highlight: "Poupa mais de €10/mês",
    features: [
      "Escolhe até 6 módulos",
      "Questões ilimitadas",
      "Modo exame",
      "Progresso intermédio",
    ],
    cta: "Escolher Plano",
    featured: false,
  },
  {
    id: "individual",
    name: "Módulo Individual",
    subtitle: "1 módulo",
    icon: BookOpen,
    price: "4,99",
    period: "/mês",
    highlight: null,
    features: [
      "Acesso a 1 módulo",
      "Questões ilimitadas",
      "Modo exame básico",
    ],
    cta: "Começar",
    featured: false,
  },
];

const VARIANTS = {
  en: {
    sectionLabel: "L4C PART 66 APP",
    heading: "Choose the plan that's right for your Part 66 certification",
    subheading: "Full access to every module or pick only the ones you need — study at your own pace.",
    tryFree: "Try the App for free \u2192",
    flexName: "Flex Pack",
    flexSub: "up to 6 modules",
    flexPeriod: "/mo",
    flexSave: "Save over €10/month",
    flexFeats: ["Choose up to 6 modules", "Unlimited questions", "Exam mode", "Intermediate progress"],
    flexCta: "Choose Plan",
    fullName: "Full Access",
    fullSub: "all 17 modules",
    fullPopular: "Most Popular",
    fullSave: "Save up to 22%",
    fullFeats: [
      "Access to all 17 modules",
      "10000+ questions",
      "Real exam simulation",
      "\"Failed Questions\" mode",
      "Detailed progress",
      "Updates included",
    ],
    fullCtaIdle: "Get Started",
    fullCtaLoading: "Processing...",
    indivName: "Single Module",
    indivSub: "1 module",
    indivPeriod: "/mo",
    indivCta: "Start",
    fullOptions: [
      { label: "Monthly", period: "/mo", equiv: null, tag: null },
      { label: "3 Months", period: "/3 mo", equiv: "\u2248 €13.33/mo", tag: null },
      { label: "6 Months", period: "/6 mo", equiv: "\u2248 €11.66/mo", tag: "Best value" },
    ],
    paymentError: "Failed to create payment session.",
    processError: "Error processing. Please try again.",
  },
  pt: {
    sectionLabel: "APP L4C PART 66",
    heading: "Escolhe o plano ideal para a tua certificação Part 66",
    subheading: "Acesso completo a todos os módulos ou escolhe apenas os que precisas — estuda ao teu ritmo.",
    tryFree: "Experimenta a App gratuitamente \u2192",
    flexName: "Pack Flex",
    flexSub: "até 6 módulos",
    flexPeriod: "/mês",
    flexSave: "Poupa mais de €10/mês",
    flexFeats: ["Acesso a até 6 módulos", "Questões ilimitadas", "Modo exame", "Progresso intermédio"],
    flexCta: "Escolher Plano",
    fullName: "Acesso Total",
    fullSub: "todos os 17 módulos",
    fullPopular: "Mais Popular",
    fullSave: "Poupe até 22%",
    fullFeats: [
      "Acesso a todos os 17 módulos",
      "10000+ questões",
      "Simulação real de exame",
      "Modo \"Failed Questions\"",
      "Progresso detalhado",
      "Atualizações incluídas",
    ],
    fullCtaIdle: "Começar Agora",
    fullCtaLoading: "A processar...",
    indivName: "Módulo Individual",
    indivSub: "1 módulo",
    indivPeriod: "/mês",
    indivCta: "Começar",
    fullOptions: [
      { label: "Mensal", period: "/mês", equiv: null, tag: null },
      { label: "3 Meses", period: "/3 meses", equiv: "\u2248 €13,33/mês", tag: null },
      { label: "6 Meses", period: "/6 meses", equiv: "\u2248 €11,66/mês", tag: "Melhor valor" },
    ],
    paymentError: "Erro ao criar sessão de pagamento.",
    processError: "Erro ao processar. Tenta novamente.",
  },
  es: {
    sectionLabel: "APP L4C PART 66",
    heading: "Elige el plan ideal para tu certificación Part 66",
    subheading: "Acceso completo a todos los módulos o elige solo los que necesitas — estudia a tu ritmo.",
    tryFree: "Prueba la App gratis \u2192",
    flexName: "Pack Flex",
    flexSub: "hasta 6 módulos",
    flexPeriod: "/mes",
    flexSave: "Ahorra más de €10/mes",
    flexFeats: ["Acceso a hasta 6 módulos", "Preguntas ilimitadas", "Modo examen", "Progreso intermedio"],
    flexCta: "Elegir Plan",
    fullName: "Acceso Total",
    fullSub: "los 17 módulos",
    fullPopular: "Más Popular",
    fullSave: "Ahorra hasta 22%",
    fullFeats: [
      "Acceso a los 17 módulos",
      "10000+ preguntas",
      "Simulación real de examen",
      "Modo \"Failed Questions\"",
      "Progreso detallado",
      "Actualizaciones incluidas",
    ],
    fullCtaIdle: "Empezar Ahora",
    fullCtaLoading: "Procesando...",
    indivName: "Módulo Individual",
    indivSub: "1 módulo",
    indivPeriod: "/mes",
    indivCta: "Empezar",
    fullOptions: [
      { label: "Mensual", period: "/mes", equiv: null, tag: null },
      { label: "3 Meses", period: "/3 meses", equiv: "\u2248 €13,33/mes", tag: null },
      { label: "6 Meses", period: "/6 meses", equiv: "\u2248 €11,66/mes", tag: "Mejor precio" },
    ],
    paymentError: "Error al crear la sesión de pago.",
    processError: "Error al procesar. Inténtalo de nuevo.",
  },
};

export default function PricingSection({ lockedLang }) {
  const [selectedOption, setSelectedOption] = useState(2);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const effectiveLang = lockedLang && VARIANTS[lockedLang] ? lockedLang : "pt";
  const baseT = VARIANTS[effectiveLang];
  const spinner = <Loader2 className="w-4 h-4 animate-spin mx-auto" />;
  const T = {
    ...baseT,
    flexLoading: spinner,
    indivLoading: spinner,
  };

  const fullOpts = T.fullOptions.map((o, i) => ({ ...fullAccessOptions[i], ...o }));

  const handleCheckout = async (packageId) => {
    setLoadingPlan(packageId);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(T.paymentError);
      }
    } catch {
      toast.error(T.processError);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="app-precos" data-testid="pricing-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A8FF] mb-2">{T.sectionLabel}</p>
          <div className="section-divider mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1628] mb-4">
            {T.heading}
          </h2>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {T.subheading}
          </p>
          <Link
            to="/app"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#00A8FF] hover:text-[#0A1628] transition-colors mt-3"
            data-testid="try-app-link"
          >
            {T.tryFree}
          </Link>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">

          {/* CARD LEFT — Pack Flex */}
          <div
            className="rounded-xl border border-slate-200 bg-slate-50/80 p-8 transition-all duration-300 hover:shadow-lg hover:border-slate-300 mt-0 lg:mt-8"
            data-testid="pricing-card-flex"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1628]">{T.flexName}</h3>
                <p className="text-xs text-slate-400">{T.flexSub}</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-slate-400">€</span>
                <span className="font-['Outfit'] text-4xl font-black text-[#0A1628]">11,99</span>
                <span className="text-sm text-slate-400">{T.flexPeriod}</span>
              </div>
              <p className="text-xs font-semibold text-[#F59E0B] mt-2">{T.flexSave}</p>
            </div>

            <ul className="space-y-3 mb-8">
              {T.flexFeats.map((f, j) => (
                <li key={j} className="flex items-center gap-2.5 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-[#F59E0B] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCheckout("flex")}
              disabled={loadingPlan === "flex"}
              className="w-full block text-center font-bold py-3 text-sm rounded-lg transition-all duration-300 bg-transparent text-[#0A1628] border-2 border-[#0A1628] hover:bg-[#0A1628] hover:text-white disabled:opacity-50"
              data-testid="pricing-cta-flex"
            >
              {loadingPlan === "flex" ? T.flexLoading : T.flexCta}
            </button>
          </div>

          {/* CARD CENTER — Acesso Total (FEATURED) */}
          <div
            className="relative rounded-xl border-2 border-[#F59E0B] bg-white p-8 md:p-10 transition-all duration-300 shadow-xl shadow-amber-500/10 hover:shadow-2xl hover:shadow-amber-500/15 lg:scale-105 z-10"
            data-testid="pricing-card-full"
          >
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-xs font-bold py-1.5 px-5 rounded-full flex items-center gap-1.5 shadow-lg shadow-amber-500/20">
              <Star className="w-3.5 h-3.5 fill-white" />
              {T.fullPopular}
            </div>

            {/* Save badge */}
            <div className="absolute top-4 right-4">
              <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                {T.fullSave}
              </span>
            </div>

            <div className="flex items-center gap-3 mb-6 mt-2">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-xl font-bold text-[#0A1628]">{T.fullName}</h3>
                <p className="text-xs text-slate-400">{T.fullSub}</p>
              </div>
            </div>

            {/* Period selector */}
            <div className="flex gap-1.5 bg-slate-100 rounded-lg p-1 mb-6">
              {fullOpts.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedOption(i)}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-bold transition-all duration-200 relative ${
                    selectedOption === i
                      ? "bg-white text-[#0A1628] shadow-sm"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                  data-testid={`pricing-toggle-${i}`}
                >
                  {opt.label}
                  {opt.tag && selectedOption === i && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-[#F59E0B] whitespace-nowrap">
                      {opt.tag}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Price display */}
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-sm text-slate-400">€</span>
                <span className="font-['Outfit'] text-5xl font-black text-[#0A1628] transition-all duration-300">
                  {fullOpts[selectedOption].price}
                </span>
                <span className="text-sm text-slate-400">{fullOpts[selectedOption].period}</span>
              </div>
              {fullOpts[selectedOption].equiv && (
                <p className="text-xs text-slate-400 mt-1">{fullOpts[selectedOption].equiv}</p>
              )}
            </div>

            <ul className="space-y-3 mb-8">
              {T.fullFeats.map((f, j) => (
                <li key={j} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-[#00A8FF] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCheckout(fullOpts[selectedOption].packageId)}
              disabled={loadingPlan?.startsWith("full")}
              className="w-full text-center font-bold py-3.5 text-sm rounded-lg transition-all duration-300 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-0.5 disabled:opacity-50"
              data-testid="pricing-cta-full"
            >
              {loadingPlan?.startsWith("full") ? T.fullCtaLoading : T.fullCtaIdle}
            </button>
          </div>

          {/* CARD RIGHT — Módulo Individual */}
          <div
            className="rounded-xl border border-slate-200 bg-slate-50/80 p-8 transition-all duration-300 hover:shadow-lg hover:border-slate-300 mt-0 lg:mt-8"
            data-testid="pricing-card-individual"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00A8FF]/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#00A8FF]" />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1628]">{T.indivName}</h3>
                <p className="text-xs text-slate-400">{T.indivSub}</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-slate-400">€</span>
                <span className="font-['Outfit'] text-4xl font-black text-[#0A1628]">4,99</span>
                <span className="text-sm text-slate-400">{T.indivPeriod}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {plans[1].features.map((f, j) => (
                <li key={j} className="flex items-center gap-2.5 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-[#00A8FF] shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCheckout("individual")}
              disabled={loadingPlan === "individual"}
              className="w-full block text-center font-bold py-3 text-sm rounded-lg transition-all duration-300 bg-transparent text-[#0A1628] border-2 border-[#0A1628] hover:bg-[#0A1628] hover:text-white disabled:opacity-50"
              data-testid="pricing-cta-individual"
            >
              {loadingPlan === "individual" ? T.indivLoading : T.indivCta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
